import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Wrench, MapPin, User, Calendar, AlertCircle, Package, Gauge, CheckSquare, History, ClipboardList, Trash2, X, Image, ChevronRight, Clock, Edit3 } from 'lucide-react';
import EquipmentRepairs from './EquipmentRepairs';
import ServiceHistory from './ServiceHistory';
import { CustomSelect } from '../common/CustomSelect';
import { useLocation as useGpsLocation } from '../../contexts/LocationContext';

interface Equipment {
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  serial_number: string;
  equipment_type: string;
  year: number;
  status: string;
  current_job_name?: string;
  current_job_number?: string;
  assigned_first_name?: string;
  assigned_last_name?: string;
  last_inspection?: string;
  next_inspection_due?: string;
  hours_last_serviced?: number;
  notes?: string;
  open_issues_count?: number;
  telematics_current_hours?: number | null;
  telematics_last_lat?: number | null;
  telematics_last_lng?: number | null;
  telematics_ignition_on?: boolean | null;
  telematics_synced_at?: string | null;
  latest_telematics?: {
    hours: number;
    fuel_level?: number;
    lat?: number;
    lng?: number;
    current_job_id?: number | null;
    current_job_name?: string | null;
    current_job_number?: string | null;
    location_lat: number;
    location_lng: number;
    timestamp: string;
  };
}

interface Issue {
  issue_id: number;
  severity: string;
  priority?: string;
  issue_description: string;
  reported_date: string;
  reporter_first_name: string;
  reporter_last_name: string;
  job_name?: string;
  is_fixed: boolean;
  issue_photos?: string;
  parts_needed?: string;
  parts_status?: string;
}

interface OpenInspection {
  inspection_id: number;
  equipment_id: number;
  equipment_name: string;
  bradanna_equipment_number: string;
  equipment_type: string;
  inspection_status: string;
  current_hours: number;
  inspection_date: string;
  last_updated: string;
  performed_by: string;
  template_name: string;
}

interface EquipmentFilter {
  equipment_filter_id: number;
  equipment_id: number;
  filter_type_id: number;
  filter_type_name: string;
  part_number: string;
  manufacturer?: string;
  service_interval_hours?: number;
  is_active: boolean;
  notes?: string;
  last_changed_date?: string;
  last_changed_hours?: number;
}

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// Language support
const translations = {
  en: {
    title: 'Machine Hub',
    selectEquipment: 'Select Equipment',
    startInspection: 'Start Inspection',
    overview: 'Overview',
    status: 'Status',
    currentJob: 'Current Job',
    assignedTo: 'Assigned To',
    serialNumber: 'Serial Number',
    year: 'Year',
    type: 'Type',
    openIssues: 'Open Issues',
    viewIssues: 'View Issues',
    filterReference: 'Filter Reference',
    oilFilter: 'Engine Oil Filter',
    fuelFilter: 'Fuel Filter',
    airFilter: 'Air Filter',
    hydraulicFilter: 'Hydraulic Filter',
    cabinAirFilter: 'Cabin Air Filter',
    transmissionFilter: 'Transmission Filter',
    defFilter: 'DEF Filter',
    waterSeparatorFilter: 'Water Separator Filter',
    belt: 'Belt',
    editFilters: 'Edit Filters',
    saveFilters: 'Save Filters',
    cancel: 'Cancel',
    telematics: 'Telematics Data',
    engineHours: 'Engine Hours',
    fuelLevel: 'Fuel Level',
    lastUpdate: 'Last Update',
    noData: 'No data available',
    inspection: 'Inspection',
    lastInspection: 'Last Inspection',
    nextDue: 'Next Due',
    hoursLastServiced: 'Hours at Last Service',
    hoursSinceInspection: 'Hours Since Inspection',
    maintenanceDue: 'Maintenance Due',
    overdueBy: 'overdue',
    dueIn: 'due in',
    hrs: 'hrs',
    notes: 'Notes',
    available: 'Available',
    inUse: 'In Use',
    needsRepair: 'Needs Repair',
    outOfService: 'Out of Service',
    shop: 'Shop',
    unassigned: 'Unassigned',
    openInspections: 'Open Inspections'
  },
  es: {
    title: 'Centro de Máquinas',
    selectEquipment: 'Seleccionar Equipo',
    startInspection: 'Iniciar Inspección',
    overview: 'Resumen',
    status: 'Estado',
    currentJob: 'Trabajo Actual',
    assignedTo: 'Asignado a',
    serialNumber: 'Número de Serie',
    year: 'Año',
    type: 'Tipo',
    openIssues: 'Problemas Abiertos',
    viewIssues: 'Ver Problemas',
    filterReference: 'Referencia de Filtros',
    oilFilter: 'Filtro de Aceite de Motor',
    fuelFilter: 'Filtro de Combustible',
    airFilter: 'Filtro de Aire',
    hydraulicFilter: 'Filtro Hidráulico',
    cabinAirFilter: 'Filtro de Aire de Cabina',
    transmissionFilter: 'Filtro de Transmisión',
    defFilter: 'Filtro DEF',
    waterSeparatorFilter: 'Filtro Separador de Agua',
    belt: 'Correa',
    editFilters: 'Editar Filtros',
    saveFilters: 'Guardar Filtros',
    cancel: 'Cancelar',
    telematics: 'Datos Telemáticos',
    engineHours: 'Horas de Motor',
    fuelLevel: 'Nivel de Combustible',
    lastUpdate: 'Última Actualización',
    noData: 'No hay datos disponibles',
    inspection: 'Inspección',
    lastInspection: 'Última Inspección',
    nextDue: 'Próximo Vencimiento',
    hoursLastServiced: 'Horas en Último Servicio',
    hoursSinceInspection: 'Horas Desde Inspección',
    maintenanceDue: 'Mantenimiento Pendiente',
    overdueBy: 'vencido',
    dueIn: 'vence en',
    hrs: 'hrs',
    notes: 'Notas',
    available: 'Disponible',
    inUse: 'En Uso',
    needsRepair: 'Necesita Reparación',
    outOfService: 'Fuera de Servicio',
    shop: 'Taller',
    unassigned: 'Sin Asignar',
    openInspections: 'Inspecciones Abiertas'
  }
};

export const MachineHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [language, setLanguage] = useState<'en' | 'es'>(() => {
    return (localStorage.getItem('language') as 'en' | 'es') || 'en';
  });

  const [allEquipment, setAllEquipment] = useState<Equipment[]>([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number | null>(() => {
    // Initialize from URL parameter if present
    const equipmentId = searchParams.get('equipment');
    return equipmentId ? parseInt(equipmentId) : null;
  });
  const [equipmentDetails, setEquipmentDetails] = useState<Equipment | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState<'hub' | 'repair' | 'history'>('hub');
  const [openInspections, setOpenInspections] = useState<OpenInspection[]>([]);
  const [viewingPhotos, setViewingPhotos] = useState<string[] | null>(null);
  const [fixingIssue, setFixingIssue] = useState<Issue | null>(null);
  const [fixDescription, setFixDescription] = useState('');
  // Inline severity editing for an open issue.
  const [editingIssueId, setEditingIssueId] = useState<number | null>(null);
  const [editSeverity, setEditSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [savingSeverity, setSavingSeverity] = useState(false);
  const [equipmentFilters, setEquipmentFilters] = useState<EquipmentFilter[]>([]);
  const [allFilterTypes, setAllFilterTypes] = useState<any[]>([]);
  const [addingFilter, setAddingFilter] = useState(false);
  const [editingFilter, setEditingFilter] = useState<EquipmentFilter | null>(null);
  const [filterFormData, setFilterFormData] = useState({
    filter_type_id: 0,
    part_number: '',
    manufacturer: '',
    service_interval_hours: '',
    is_active: true
  });
  const [viewingFilter, setViewingFilter] = useState<EquipmentFilter | null>(null);

  // Search state
  const [searchText, setSearchText] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);

  // Phone GPS — powers the "At Your Location" quick-pick list. Nearly every
  // machine has a tracker now, so the machines you're standing next to are
  // almost always the ones you came here for.
  const { location: gpsLocation, ensureFreshLocation } = useGpsLocation();
  useEffect(() => { ensureFreshLocation().catch(() => { /* section just hides */ }); }, []);
  // Machines within walking distance of the phone, closest first. 800m covers
  // a big job site without pulling in the next site over.
  const NEARBY_RADIUS_M = 800;
  const nearbyEquipment = useMemo(() => {
    if (!gpsLocation) return [];
    const R = 6_371_000, p = Math.PI / 180;
    const dist = (aLat: number, aLng: number, bLat: number, bLng: number) => {
      const dLa = (bLat - aLat) * p, dLo = (bLng - aLng) * p;
      const h = Math.sin(dLa / 2) ** 2 + Math.cos(aLat * p) * Math.cos(bLat * p) * Math.sin(dLo / 2) ** 2;
      return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
    };
    return allEquipment
      .filter(eq => eq.telematics_last_lat != null && eq.telematics_last_lng != null)
      .map(eq => ({ eq, d: dist(gpsLocation.latitude, gpsLocation.longitude, Number(eq.telematics_last_lat), Number(eq.telematics_last_lng)) }))
      .filter(x => x.d <= NEARBY_RADIUS_M)
      .sort((a, b) => a.d - b.d)
      .slice(0, 12);
  }, [allEquipment, gpsLocation]);
  const fmtDist = (m: number) => (m < 300 ? `${Math.max(10, Math.round(m * 3.281 / 10) * 10)} ft` : `${(m / 1609).toFixed(1)} mi`);

  const t = translations[language];

  // Styles
  const styles = {
    container: {
      padding: '20px',
      paddingBottom: '400px',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      backgroundColor: '#ea580c',
      color: 'white',
      padding: '16px',
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    headerTitle: {
      fontSize: '20px',
      fontWeight: '600',
      flex: 1
    },
    content: {
      backgroundColor: 'white',
      padding: '24px',
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    },
    select: {
      width: '100%',
      padding: '12px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white',
      cursor: 'pointer',
      marginBottom: '24px'
    },
    searchContainer: {
      position: 'relative' as const,
      marginBottom: '24px'
    },
    searchInput: {
      width: '100%',
      padding: '14px',
      fontSize: '20px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      backgroundColor: 'white',
      boxSizing: 'border-box' as const,
      fontWeight: '500' as const
    },
    dropdown: {
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      marginTop: '4px',
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      maxHeight: '300px',
      overflowY: 'auto' as const,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 1000
    },
    dropdownItem: {
      padding: '14px',
      cursor: 'pointer',
      borderBottom: '1px solid #f1f5f9',
      fontSize: '16px'
    },
    dropdownItemName: {
      fontWeight: '600' as const,
      color: '#1e293b',
      marginBottom: '6px',
      fontSize: '18px'
    },
    dropdownItemDetails: {
      fontSize: '15px',
      color: '#64748b'
    },
    section: {
      marginBottom: '24px'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    card: {
      backgroundColor: '#f8fafc',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
      paddingBottom: '12px',
      borderBottom: '1px solid #e5e7eb'
    },
    label: {
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '500'
    },
    value: {
      fontSize: '16px',
      color: '#1e293b',
      fontWeight: '600',
      textAlign: 'right' as const
    },
    statusBadge: (status: string) => {
      const colors = {
        available: { bg: '#dcfce7', border: '#86efac', text: '#166534' },
        in_use: { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
        needs_repair: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
        out_of_service: { bg: '#f3f4f6', border: '#d1d5db', text: '#4b5563' }
      };
      const color = colors[status as keyof typeof colors] || colors.available;
      return {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: color.bg,
        border: `2px solid ${color.border}`,
        color: color.text
      };
    },
    issuesBadge: (count: number) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '8px 16px',
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: '700',
      backgroundColor: count > 0 ? '#fee2e2' : '#dcfce7',
      border: `2px solid ${count > 0 ? '#fca5a5' : '#86efac'}`,
      color: count > 0 ? '#991b1b' : '#166534'
    }),
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px'
    },
    filterItem: {
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      padding: '12px'
    },
    filterLabel: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '500',
      marginBottom: '4px'
    },
    filterValue: {
      fontSize: '14px',
      color: '#1e293b',
      fontWeight: '600'
    },
    telematicsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px'
    },
    telemetricItem: {
      textAlign: 'center' as const,
      padding: '16px',
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '8px'
    },
    telemetricValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#ea580c',
      marginBottom: '4px'
    },
    telemetricLabel: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '500'
    },
    noData: {
      textAlign: 'center' as const,
      padding: '32px',
      color: '#9ca3af',
      fontSize: '14px'
    }
  };

  // Load equipment list
  useEffect(() => {
    const userData = localStorage.getItem('bfa_user');
    if (userData) {
      loadEquipment();
      loadOpenInspections();
    }

    const langData = localStorage.getItem('language');
    if (langData === 'en' || langData === 'es') {
      setLanguage(langData);
    }

    const handleLanguageChange = (event: Event) => {
      const newLang = (event as CustomEvent).detail;
      if (newLang === 'en' || newLang === 'es') {
        setLanguage(newLang);
      }
    };
    window.addEventListener('languageChange', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange);
    };
  }, []);

  // Load equipment details when selection changes
  useEffect(() => {
    if (selectedEquipmentId) {
      loadEquipmentDetails(selectedEquipmentId);
      loadEquipmentIssues(selectedEquipmentId);
    }
  }, [selectedEquipmentId]);

  // Deep-link from Fleet view (?equipment=<id>) pre-selects a machine but leaves
  // the search bar empty. Fill it with the machine's name once equipment loads,
  // so the search box reflects the selection — same as picking from the dropdown.
  const didDeepLinkFillRef = useRef(false);
  useEffect(() => {
    if (didDeepLinkFillRef.current) return;
    if (!selectedEquipmentId || allEquipment.length === 0) return;
    const eq = allEquipment.find(e => e.equipment_id === selectedEquipmentId);
    if (eq) {
      setSearchText(`${eq.equipment_name} (#${eq.bradanna_equipment_number})`);
      didDeepLinkFillRef.current = true;
    }
  }, [selectedEquipmentId, allEquipment]);

  // Filter equipment based on search text
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredEquipment(allEquipment);
    } else {
      const search = searchText.toLowerCase();
      const filtered = allEquipment.filter(eq =>
        eq.equipment_name.toLowerCase().includes(search) ||
        eq.bradanna_equipment_number.toLowerCase().includes(search) ||
        eq.equipment_type.toLowerCase().includes(search)
      );
      setFilteredEquipment(filtered);
    }
  }, [searchText, allEquipment]);

  // Scroll to top when returning to hub view
  useEffect(() => {
    if (currentView === 'hub') {
      window.scrollTo(0, 0);
    }
  }, [currentView]);

  // Handle browser/native back button for internal views
  useEffect(() => {
    const handlePopState = () => {
      // If we're in a sub-view, go back to hub
      if (currentView !== 'hub') {
        setCurrentView('hub');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentView]);

  // Push history state when entering sub-views
  const changeView = (view: 'hub' | 'repair' | 'history') => {
    if (view !== 'hub' && currentView === 'hub') {
      // Push a history entry when going from hub to sub-view
      window.history.pushState({ view }, '', window.location.pathname);
    }
    setCurrentView(view);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-search-container]')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadEquipment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAllEquipment(data.data);
      }
    } catch (error) {
      console.error('Error loading equipment:', error);
    }
  };

  const loadEquipmentDetails = async (equipmentId: number) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/details`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEquipmentDetails(data.data);
      }

      // Load equipment filters
      const filtersResponse = await fetch(`${API_BASE_URL}/api/inventory/equipment/${equipmentId}/filters`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (filtersResponse.ok) {
        const filtersData = await filtersResponse.json();
        setEquipmentFilters(filtersData.data || []);
      }
    } catch (error) {
      console.error('Error loading equipment details:', error);
    }
    setLoading(false);
  };

  const loadEquipmentIssues = async (equipmentId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/${equipmentId}/issues`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIssues(data.data.filter((issue: Issue) => !issue.is_fixed));
      }
    } catch (error) {
      console.error('Error loading equipment issues:', error);
    }
  };

  // Severity (Low/Medium/High) maps to the canonical minor/moderate/critical on
  // the backend. severityToLevel converts a stored value back for the dropdown.
  const severityToLevel = (sev: string): 'low' | 'medium' | 'high' =>
    sev === 'critical' || sev === 'high' ? 'high' : sev === 'moderate' || sev === 'medium' ? 'medium' : 'low';

  const startEditSeverity = (issue: Issue) => {
    setEditingIssueId(issue.issue_id);
    setEditSeverity(severityToLevel(issue.severity));
  };

  const saveIssueSeverity = async (issueId: number) => {
    setSavingSeverity(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/issues/${issueId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ severity: editSeverity }),
      });
      if (response.ok) {
        setEditingIssueId(null);
        if (selectedEquipmentId) loadEquipmentIssues(selectedEquipmentId);
      } else {
        console.error('Failed to save severity:', await response.text());
      }
    } catch (error) {
      console.error('Error saving severity:', error);
    } finally {
      setSavingSeverity(false);
    }
  };

  const loadOpenInspections = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/inspections/open`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOpenInspections(data.data || []);
      }
    } catch (error) {
      console.error('Error loading open inspections:', error);
    }
  };

  const handleDeleteInspection = async (inspectionId: number, equipmentName: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation when clicking delete

    if (!confirm(`Delete draft inspection for ${equipmentName}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/equipment/inspection/${inspectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('bfa_token')}`
        }
      });

      if (response.ok) {
        // Reload open inspections
        loadOpenInspections();
      } else {
        alert('Failed to delete inspection');
      }
    } catch (error) {
      console.error('Error deleting inspection:', error);
      alert('Failed to delete inspection');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return t.noData;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/New_York'
    });
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      available: t.available,
      in_use: t.inUse,
      needs_repair: t.needsRepair,
      out_of_service: t.outOfService
    };
    return statusMap[status] || status;
  };

  const handleSelectEquipment = (equipment: Equipment) => {
    setSelectedEquipmentId(equipment.equipment_id);
    setSearchText(`${equipment.equipment_name} (#${equipment.bradanna_equipment_number})`);
    setShowDropdown(false);
    // Update URL with equipment ID so back button works
    setSearchParams({ equipment: equipment.equipment_id.toString() });
  };

  const handleClearSelection = () => {
    setSelectedEquipmentId(null);
    setEquipmentDetails(null);
    setSearchText('');
    setShowDropdown(false);
    // Clear URL parameter
    setSearchParams({});
    window.scrollTo(0, 0);
  };

  const handleBackToHub = () => {
    setCurrentView('hub');
    // Don't clear selection - just show hub view with selected equipment
  };

  const handleFixIssue = async () => {
    if (!fixingIssue) return;

    try {
      const token = localStorage.getItem('bfa_token');
      const response = await fetch(`${API_BASE_URL}/api/equipment/issues/${fixingIssue.issue_id}/close-from-inspection`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fix_description: fixDescription })
      });

      if (response.ok) {
        // Reload issues
        if (selectedEquipmentId) {
          loadEquipmentIssues(selectedEquipmentId);
        }
        setFixingIssue(null);
        setFixDescription('');
        alert('Issue marked as fixed!');
      } else {
        alert('Failed to mark issue as fixed');
      }
    } catch (error) {
      console.error('Error fixing issue:', error);
      alert('Failed to mark issue as fixed');
    }
  };

  const handleViewPhotos = (photosJson: string) => {
    try {
      const photos = JSON.parse(photosJson);
      setViewingPhotos(Array.isArray(photos) ? photos : []);
    } catch (error) {
      console.error('Error parsing photos:', error);
      setViewingPhotos([]);
    }
  };

  const handleAddFilter = async () => {
    // Load all filter types if not loaded
    if (allFilterTypes.length === 0) {
      try {
        const token = localStorage.getItem('bfa_token');
        const response = await fetch(`${API_BASE_URL}/api/inventory/filters/types`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAllFilterTypes(data.data || []);
        }
      } catch (error) {
        console.error('Error loading filter types:', error);
      }
    }

    setFilterFormData({
      filter_type_id: 0,
      part_number: '',
      manufacturer: '',
      service_interval_hours: '',
      is_active: true
    });
    setAddingFilter(true);
  };

  const handleEditFilter = (filter: EquipmentFilter) => {
    setEditingFilter(filter);
    setFilterFormData({
      filter_type_id: filter.filter_type_id,
      part_number: filter.part_number || '',
      manufacturer: filter.manufacturer || '',
      service_interval_hours: filter.service_interval_hours?.toString() || '',
      is_active: filter.is_active
    });
  };

  const handleSaveFilter = async () => {
    if (!selectedEquipmentId) return;

    try {
      const token = localStorage.getItem('bfa_token');
      const payload = {
        filter_type_id: filterFormData.filter_type_id,
        part_number: filterFormData.part_number,
        manufacturer: filterFormData.manufacturer,
        service_interval_hours: filterFormData.service_interval_hours ? parseInt(filterFormData.service_interval_hours) : null,
        is_active: filterFormData.is_active
      };

      if (editingFilter) {
        // Update existing filter
        await fetch(`${API_BASE_URL}/api/inventory/equipment/filters/${editingFilter.equipment_filter_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        // Add new filter
        await fetch(`${API_BASE_URL}/api/inventory/equipment/${selectedEquipmentId}/filters`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      // Reload filters
      loadEquipmentDetails(selectedEquipmentId);
      setAddingFilter(false);
      setEditingFilter(null);
      alert(editingFilter ? 'Filter updated successfully!' : 'Filter added successfully!');
    } catch (error) {
      console.error('Error saving filter:', error);
      alert('Failed to save filter');
    }
  };

  const handleDeleteFilter = async (filterId: number) => {
    if (!confirm('Delete this filter?')) return;

    try {
      const token = localStorage.getItem('bfa_token');
      await fetch(`${API_BASE_URL}/api/inventory/equipment/filters/${filterId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Reload filters
      if (selectedEquipmentId) {
        loadEquipmentDetails(selectedEquipmentId);
      }
      alert('Filter deleted successfully!');
    } catch (error) {
      console.error('Error deleting filter:', error);
      alert('Failed to delete filter');
    }
  };

  // Show repair or history view if selected
  if (currentView === 'repair' && equipmentDetails) {
    return (
      <EquipmentRepairs
        equipment={{
          equipment_id: equipmentDetails.equipment_id,
          equipment_name: equipmentDetails.equipment_name,
          bradanna_equipment_number: equipmentDetails.bradanna_equipment_number,
          equipment_type: equipmentDetails.equipment_type,
        }}
        onBack={handleBackToHub}
      />
    );
  }

  if (currentView === 'history' && equipmentDetails) {
    return (
      <ServiceHistory
        equipment={{
          equipment_id: equipmentDetails.equipment_id,
          equipment_name: equipmentDetails.equipment_name,
          bradanna_equipment_number: equipmentDetails.bradanna_equipment_number,
          equipment_type: equipmentDetails.equipment_type,
        }}
        onBack={handleBackToHub}
      />
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Wrench size={24} />
        <h1 style={styles.headerTitle}>{t.title}</h1>
        <button
          onClick={() => navigate('/mechanics/open-inspections')}
          style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          <ClipboardList size={18} />
          {t.openInspections}
        </button>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Searchable Equipment Selector */}
        <div style={styles.searchContainer} data-search-container>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => {
              setSearchText('');
              setSelectedEquipmentId(null);
              setEquipmentDetails(null);
              setShowDropdown(true);
            }}
            placeholder={`${t.selectEquipment}...`}
            style={styles.searchInput}
          />
          {showDropdown && searchText && filteredEquipment.length > 0 && (
            <div style={styles.dropdown}>
              {filteredEquipment.map((eq) => (
                <div
                  key={eq.equipment_id}
                  onClick={() => handleSelectEquipment(eq)}
                  style={{
                    ...styles.dropdownItem,
                    backgroundColor: selectedEquipmentId === eq.equipment_id ? '#f8fafc' : 'white'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = selectedEquipmentId === eq.equipment_id ? '#f8fafc' : 'white';
                  }}
                >
                  <div style={styles.dropdownItemName}>
                    {eq.equipment_name} (#{eq.bradanna_equipment_number})
                  </div>
                  <div style={styles.dropdownItemDetails}>
                    {eq.equipment_type}
                  </div>
                </div>
              ))}
            </div>
          )}
          {showDropdown && searchText && filteredEquipment.length === 0 && (
            <div style={styles.dropdown}>
              <div style={{ ...styles.dropdownItem, textAlign: 'center', color: '#9ca3af' }}>
                No equipment found
              </div>
            </div>
          )}
        </div>

        {/* At Your Location — machines whose GPS puts them within walking
            distance of the phone, closest first. Replaces "Recently Viewed":
            the machine you want is almost always the one in front of you. */}
        {!selectedEquipmentId && nearbyEquipment.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <MapPin size={20} color="#ea580c" />
              At Your Location
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {nearbyEquipment.map(({ eq, d }) => (
                <button
                  key={eq.equipment_id}
                  onClick={() => handleSelectEquipment(eq)}
                  style={{
                    padding: '12px 14px',
                    backgroundColor: 'white',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    textAlign: 'left' as const,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#ea580c';
                    e.currentTarget.style.backgroundColor = '#fff7ed';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <span style={{ width: 11, height: 11, borderRadius: '50%', flexShrink: 0, background: eq.telematics_ignition_on ? '#16a34a' : '#94a3b8' }} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                    {eq.equipment_name}
                    <span style={{ marginLeft: 6, color: '#64748b', fontWeight: 500, fontSize: '14px' }}>{eq.bradanna_equipment_number}</span>
                  </span>
                  <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{fmtDist(d)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Open Inspections Section */}
        {!selectedEquipmentId && openInspections.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <ClipboardList size={20} color="#ea580c" />
              Draft Inspections — tap to continue
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {openInspections.map((inspection) => (
                <div
                  key={inspection.inspection_id}
                  onClick={() => navigate(`/mechanics/inspection/${inspection.equipment_id}`)}
                  style={{
                    backgroundColor: 'white',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#ea580c';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(234, 88, 12, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                        {inspection.equipment_name}
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                        #{inspection.bradanna_equipment_number} • {inspection.equipment_type}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        textTransform: 'uppercase'
                      }}>
                        {inspection.inspection_status === 'draft' ? 'Draft' : 'In Progress'}
                      </div>
                      <button
                        onClick={(e) => handleDeleteInspection(inspection.inspection_id, inspection.equipment_name, e)}
                        style={{
                          padding: '8px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#ef4444';
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
                    <div>Started: {new Date(inspection.inspection_date).toLocaleDateString()}</div>
                    <div>By: {inspection.performed_by}</div>
                    {inspection.current_hours > 0 && (
                      <div>Hours: {inspection.current_hours}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {equipmentDetails && (() => {
          // This machine's open draft (if any) — the inspection page resumes it,
          // so the button reads "Continue" instead of "Start".
          const draft = openInspections.find(i => i.equipment_id === selectedEquipmentId);
          return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '24px' }}>
            {/* Report Issue — reuses the existing /equipment report form with
                this machine preselected (photos, severity, GPS job). */}
            <button
              onClick={() => navigate(`/equipment?equipment=${selectedEquipmentId}`)}
              style={{
                width: '100%',
                backgroundColor: '#dc2626',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <AlertCircle size={20} />
              Report Issue
            </button>

            {/* Start / Continue Inspection Button */}
            <button
              onClick={() => navigate(`/mechanics/inspection/${selectedEquipmentId}`)}
              style={{
                width: '100%',
                backgroundColor: '#ea580c',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(234, 88, 12, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <CheckSquare size={20} />
              {draft ? `Continue Inspection (${draft.inspection_status === 'draft' ? 'Draft' : 'In Progress'})` : t.startInspection}
            </button>

            {/* Log Repair Button */}
            <button
              onClick={() => changeView('repair')}
              style={{
                width: '100%',
                backgroundColor: '#f97316',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Wrench size={20} />
              Log Repair
            </button>

            {/* Service History Button */}
            <button
              onClick={() => changeView('history')}
              style={{
                width: '100%',
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '16px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <History size={20} />
              Service History
            </button>
          </div>
          );
        })()}

        {/* Equipment Details */}
        {loading && (
          <div style={styles.noData}>Loading...</div>
        )}

        {!loading && equipmentDetails && (
          <>
            {/* Overview Section */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <Package size={20} />
                {t.overview}
              </div>
              <div style={styles.card}>
                <div style={styles.row}>
                  <span style={styles.label}>{t.status}</span>
                  <span style={styles.statusBadge(equipmentDetails.status)}>
                    {getStatusText(equipmentDetails.status)}
                  </span>
                </div>

                {/* GPS Current Location */}
                {(() => {
                  const tele = equipmentDetails.latest_telematics
                  const gpsJob = tele?.current_job_name
                  const lat = tele?.lat
                  const lng = tele?.lng
                  if (!tele || (lat == null && lng == null && !gpsJob)) return null
                  return (
                    <div style={styles.row}>
                      <span style={styles.label}>Location <span style={{ opacity: 0.6, fontSize: 11 }}>(GPS)</span></span>
                      <span style={{ ...styles.value, textAlign: 'right' as const, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                        {gpsJob ? (
                          <>📍 {gpsJob}{tele?.current_job_number ? ` (${tele.current_job_number})` : ''}</>
                        ) : lat != null && lng != null ? (
                          <>
                            <span style={{ color: '#94a3b8' }}>Off-site</span>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#3b82f6', textDecoration: 'underline', fontSize: 13 }}
                            >Map ↗</a>
                          </>
                        ) : 'No GPS'}
                      </span>
                    </div>
                  )
                })()}

                {/* Engine Hours from GPS */}
                {(equipmentDetails.latest_telematics?.hours != null || equipmentDetails.telematics_current_hours != null) && (
                  <div style={styles.row}>
                    <span style={styles.label}>{t.engineHours} <span style={{ opacity: 0.6, fontSize: 11 }}>(GPS)</span></span>
                    <span style={styles.value}>
                      {Math.round(
                        Number(equipmentDetails.latest_telematics?.hours ?? equipmentDetails.telematics_current_hours)
                      ).toLocaleString()} hrs
                    </span>
                  </div>
                )}

                {/* Assigned employee — only for trucks */}
                {equipmentDetails.equipment_type === 'Truck' && (
                  <div style={styles.row}>
                    <span style={styles.label}>{t.assignedTo}</span>
                    <span style={styles.value}>
                      {(equipmentDetails as any).assigned_first_name
                        ? `${(equipmentDetails as any).assigned_first_name} ${(equipmentDetails as any).assigned_last_name || ''}`.trim()
                        : t.unassigned || 'Unassigned'}
                    </span>
                  </div>
                )}

                <div style={styles.row}>
                  <span style={styles.label}>{t.serialNumber}</span>
                  <span style={styles.value}>{equipmentDetails.serial_number || t.noData}</span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>{t.type}</span>
                  <span style={styles.value}>{equipmentDetails.equipment_type}</span>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>{t.year}</span>
                  <span style={styles.value}>{equipmentDetails.year || t.noData}</span>
                </div>

                {equipmentDetails.last_inspection && (
                  <div style={styles.row}>
                    <span style={styles.label}>{t.lastInspection}</span>
                    <span style={styles.value}>{formatDate(equipmentDetails.last_inspection)}</span>
                  </div>
                )}
                {equipmentDetails.hours_last_serviced != null && (
                  <div style={styles.row}>
                    <span style={styles.label}>{t.hoursLastServiced}</span>
                    <span style={styles.value}>{equipmentDetails.hours_last_serviced.toLocaleString()} hrs</span>
                  </div>
                )}
              </div>
            </div>

            {/* Open Issues — hidden when there are none. The inspection card
                immediately below stays visible because last-service / hours-
                since-inspection / due-maintenance is useful even with zero
                issues. */}
            {issues.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <AlertCircle size={20} />
                {t.openIssues}
              </div>
              <div style={styles.card}>
                <div style={{ textAlign: 'center' as const }}>
                  <div style={styles.issuesBadge(equipmentDetails.open_issues_count || 0)}>
                    <AlertCircle size={20} />
                    {equipmentDetails.open_issues_count || 0} {t.openIssues}
                  </div>
                </div>
                {issues.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    {issues.map((issue) => {
                      const photos = issue.issue_photos ? JSON.parse(issue.issue_photos) : [];
                      const hasPhotos = Array.isArray(photos) && photos.length > 0;

                      return (
                        <div
                          key={issue.issue_id}
                          style={{
                            padding: '12px',
                            backgroundColor: '#f8fafc',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            marginTop: '12px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                                {issue.severity === 'critical' && '🔴 '}{issue.severity === 'moderate' && '🟡 '}{issue.severity === 'minor' && '🟢 '}
                                {issue.issue_description}
                              </div>
                              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                                Reported by {issue.reporter_first_name} {issue.reporter_last_name} on {formatDate(issue.reported_date)}
                              </div>
                              {editingIssueId === issue.issue_id ? (
                                <div style={{ fontSize: '12px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontWeight: '600', color: '#475569' }}>Priority: </span>
                                  <select
                                    value={editSeverity}
                                    onChange={(e) => setEditSeverity(e.target.value as 'low' | 'medium' | 'high')}
                                    style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, fontWeight: 600 }}
                                  >
                                    <option value="low">Low – Minor issue</option>
                                    <option value="medium">Medium – Affects performance</option>
                                    <option value="high">High – Not operational</option>
                                  </select>
                                  <button
                                    onClick={() => saveIssueSeverity(issue.issue_id)}
                                    disabled={savingSeverity}
                                    style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    {savingSeverity ? 'Saving…' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => setEditingIssueId(null)}
                                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : issue.severity && (
                                <div style={{ fontSize: '12px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span><span style={{ fontWeight: '600', color: '#475569' }}>Priority: </span>
                                    <span style={{
                                      color: issue.severity === 'critical' ? '#dc2626' :
                                             issue.severity === 'moderate' ? '#f59e0b' : '#22c55e',
                                      fontWeight: '600'
                                    }}>
                                      {issue.severity === 'critical' ? 'High' : issue.severity === 'moderate' ? 'Medium' : 'Low'}
                                    </span>
                                  </span>
                                  <button
                                    onClick={() => startEditSeverity(issue)}
                                    style={{ padding: '2px 8px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#2563eb', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                                  >
                                    ✎ Edit
                                  </button>
                                </div>
                              )}
                              {issue.parts_needed && (
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                  <span style={{ fontWeight: '600' }}>Parts Needed: </span>{issue.parts_needed}
                                </div>
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <button
                              onClick={() => {
                                setFixingIssue(issue);
                                setFixDescription('');
                              }}
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                backgroundColor: '#22c55e',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                              }}
                            >
                              <CheckSquare size={16} />
                              Mark Fixed
                            </button>
                            {hasPhotos && (
                              <button
                                onClick={() => handleViewPhotos(issue.issue_photos || '[]')}
                                style={{
                                  padding: '8px 12px',
                                  backgroundColor: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <Image size={16} />
                                Photos ({photos.length})
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Inspection — moved up to sit right under Open Issues per
                request. Enriched with "Hours Since Inspection" (current
                tracker hours minus the hours captured at last inspection) and
                a "Maintenance Due" list derived from EQUIPMENT_FILTERS
                (last_changed_hours + service_interval_hours vs current). */}
            {(() => {
              const currentHours = equipmentDetails.latest_telematics?.hours ?? null;
              const lastServicedHours = equipmentDetails.hours_last_serviced ?? null;
              const hoursSince = currentHours != null && lastServicedHours != null
                ? Math.max(0, Math.round(currentHours - lastServicedHours))
                : null;
              // "Due" = elapsed against interval is past zero. Negative remaining
              // means overdue; positive means due-soon (we still surface anything
              // ≤ 25h out so foremen aren't blindsided the day-of).
              const DUE_SOON_WINDOW_HRS = 25;
              const maintenanceDue = (equipmentFilters || [])
                .filter(f => f.is_active && f.service_interval_hours && f.last_changed_hours != null && currentHours != null)
                .map(f => {
                  const dueAt = (f.last_changed_hours as number) + (f.service_interval_hours as number);
                  const remaining = Math.round(dueAt - (currentHours as number));
                  return { name: f.filter_type_name, remaining };
                })
                .filter(d => d.remaining <= DUE_SOON_WINDOW_HRS)
                .sort((a, b) => a.remaining - b.remaining);

              return (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>
                    <Calendar size={20} />
                    {t.inspection}
                  </div>
                  <div style={styles.card}>
                    <div style={styles.row}>
                      <span style={styles.label}>{t.lastInspection}</span>
                      <span style={styles.value}>{formatDate(equipmentDetails.last_inspection || '')}</span>
                    </div>
                    <div style={styles.row}>
                      <span style={styles.label}>{t.hoursLastServiced}</span>
                      <span style={styles.value}>
                        {lastServicedHours != null ? lastServicedHours.toLocaleString() : t.noData}
                      </span>
                    </div>
                    {hoursSince != null && (
                      <div style={styles.row}>
                        <span style={styles.label}>{t.hoursSinceInspection}</span>
                        <span style={{ ...styles.value, color: hoursSince > 250 ? '#dc2626' : hoursSince > 100 ? '#f59e0b' : '#16a34a' }}>
                          {hoursSince.toLocaleString()} {t.hrs}
                        </span>
                      </div>
                    )}
                    {maintenanceDue.length > 0 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                          {t.maintenanceDue} ({maintenanceDue.length})
                        </div>
                        {maintenanceDue.map((d, i) => {
                          const isOverdue = d.remaining < 0;
                          return (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', backgroundColor: isOverdue ? '#fef2f2' : '#fffbeb', border: `1px solid ${isOverdue ? '#fecaca' : '#fde68a'}`, borderRadius: 6, marginBottom: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{d.name}</span>
                              <span style={{ fontSize: 12, fontWeight: 700, color: isOverdue ? '#dc2626' : '#b45309' }}>
                                {isOverdue
                                  ? `${t.overdueBy} ${Math.abs(d.remaining).toLocaleString()} ${t.hrs}`
                                  : `${t.dueIn} ${d.remaining.toLocaleString()} ${t.hrs}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Filter Reference */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <Package size={20} />
                {t.filterReference}
              </div>
              {equipmentFilters.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {equipmentFilters.filter(f => f.is_active).map((filter) => (
                    <div
                      key={filter.equipment_filter_id}
                      onClick={() => setViewingFilter(filter)}
                      style={{
                        padding: '16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '12px',
                        border: '2px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#ea580c';
                        e.currentTarget.style.backgroundColor = '#fff7ed';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '6px' }}>
                            {filter.filter_type_name}
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#ea580c' }}>
                            {filter.part_number || t.noData}
                          </div>
                          {filter.last_changed_hours && (
                            <div style={{ fontSize: '14px', color: '#64748b', marginTop: '6px' }}>
                              Changed @ {filter.last_changed_hours.toLocaleString()} hrs
                            </div>
                          )}
                        </div>
                        <ChevronRight size={24} color="#9ca3af" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '16px' }}>
                  {t.noData}
                </div>
              )}
              <button
                onClick={handleAddFilter}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  padding: '14px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Package size={20} />
                Add Filter
              </button>
            </div>

            {/* Telematics Data */}
            {equipmentDetails.latest_telematics && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <Gauge size={20} />
                  {t.telematics}
                </div>
                <div style={styles.telematicsGrid}>
                  <div style={styles.telemetricItem}>
                    <div style={styles.telemetricValue}>
                      {equipmentDetails.latest_telematics.hours != null
                        ? Math.round(equipmentDetails.latest_telematics.hours).toLocaleString()
                        : '---'}
                    </div>
                    <div style={styles.telemetricLabel}>{t.engineHours}</div>
                  </div>
                </div>
                {/* GPS-based current location */}
                <div style={{ marginTop: '12px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' as const, marginBottom: '4px' }}>
                    Current Location
                  </div>
                  {equipmentDetails.latest_telematics.lat != null && equipmentDetails.latest_telematics.lng != null ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {equipmentDetails.latest_telematics.current_job_name ? (
                          <div style={{ fontSize: '15px', color: '#0f172a', fontWeight: 600 }}>
                            📍 {equipmentDetails.latest_telematics.current_job_name}
                            {equipmentDetails.latest_telematics.current_job_number ? ` (${equipmentDetails.latest_telematics.current_job_number})` : ''}
                          </div>
                        ) : (
                          <div style={{ fontSize: '14px', color: '#64748b' }}>
                            Unknown location (not in any job geofence)
                          </div>
                        )}
                      </div>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${equipmentDetails.latest_telematics.lat},${equipmentDetails.latest_telematics.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '8px 12px', background: '#3b82f6', color: '#fff',
                          fontSize: '13px', fontWeight: 600, borderRadius: '6px',
                          textDecoration: 'none', whiteSpace: 'nowrap' as const,
                        }}
                      >
                        <MapPin size={14} /> Map
                      </a>
                    </div>
                  ) : (
                    <div style={{ fontSize: '14px', color: '#94a3b8' }}>No GPS data yet</div>
                  )}
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b', textAlign: 'center' as const }}>
                  {t.lastUpdate}: {formatDate(equipmentDetails.latest_telematics.timestamp)}
                </div>
              </div>
            )}

            {/* Inspection card moved up to sit under Open Issues — see above. */}

            {/* Notes */}
            {equipmentDetails.notes && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  {t.notes}
                </div>
                <div style={styles.card}>
                  <div style={{ fontSize: '14px', color: '#1e293b', lineHeight: '1.6' }}>
                    {equipmentDetails.notes}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {!loading && !equipmentDetails && selectedEquipmentId && (
          <div style={styles.noData}>{t.noData}</div>
        )}

        {!selectedEquipmentId && (
          <div style={styles.noData}>{t.selectEquipment}</div>
        )}

        {/* Fix Issue Modal */}
        {fixingIssue && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            paddingTop: '10vh',
            paddingBottom: '40vh',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '400px',
              width: '100%',
              padding: '24px',
              flexShrink: 0
            }}>
              <h2 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Mark Issue as Fixed</h2>
              <p style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '14px' }}>
                {fixingIssue.issue_description}
              </p>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Fix Description
                </label>
                <textarea
                  value={fixDescription}
                  onChange={(e) => setFixDescription(e.target.value)}
                  placeholder="Describe what was done to fix the issue..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '8px',
                    fontSize: '14px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setFixingIssue(null);
                    setFixDescription('');
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#64748b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleFixIssue}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Mark Fixed
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Photo Viewer Modal */}
        {viewingPhotos && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1000
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              color: 'white'
            }}>
              <h2 style={{ margin: 0 }}>Issue Photos ({viewingPhotos.length})</h2>
              <button
                onClick={() => setViewingPhotos(null)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '20px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              alignContent: 'start'
            }}>
              {viewingPhotos.map((photo, index) => (
                <img
                  key={index}
                  src={photo}
                  alt={`Issue photo ${index + 1}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(photo, '_blank')}
                />
              ))}
            </div>
          </div>
        )}

        {/* View Filter Modal */}
        {viewingFilter && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px',
              paddingTop: '10vh',
              paddingBottom: '40vh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
            onClick={() => setViewingFilter(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '450px',
                width: '100%',
                overflow: 'hidden',
                flexShrink: 0
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                backgroundColor: '#ea580c',
                padding: '20px',
                color: 'white'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Filter</div>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>{viewingFilter.filter_type_name}</div>
                  </div>
                  <button
                    onClick={() => setViewingFilter(null)}
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={24} color="white" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '24px' }}>
                {/* Part Number */}
                <div style={{
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Part Number</div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#ea580c' }}>
                    {viewingFilter.part_number || 'Not set'}
                  </div>
                </div>

                {/* Manufacturer */}
                {viewingFilter.manufacturer && (
                  <div style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Manufacturer</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                      {viewingFilter.manufacturer}
                    </div>
                  </div>
                )}

                {/* Hours Info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <Clock size={24} color="#64748b" style={{ marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Hours Last Changed</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
                      {viewingFilter.last_changed_hours?.toLocaleString() || '---'}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center'
                  }}>
                    <Calendar size={24} color="#64748b" style={{ marginBottom: '8px' }} />
                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Last Changed</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {viewingFilter.last_changed_date ? formatDate(viewingFilter.last_changed_date) : '---'}
                    </div>
                  </div>
                </div>

                {/* Service Interval */}
                {viewingFilter.service_interval_hours && (
                  <div style={{
                    backgroundColor: '#dbeafe',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '14px', color: '#3b82f6', marginBottom: '4px' }}>Service Interval</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                      Every {viewingFilter.service_interval_hours.toLocaleString()} hours
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                  <button
                    onClick={() => {
                      handleEditFilter(viewingFilter);
                      setViewingFilter(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Edit3 size={20} />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteFilter(viewingFilter.equipment_filter_id);
                      setViewingFilter(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <Trash2 size={20} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Filter Modal */}
        {(addingFilter || editingFilter) && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px',
              paddingTop: '5vh',
              paddingBottom: '40vh',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}
            onClick={() => {
              setAddingFilter(false);
              setEditingFilter(null);
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '500px',
                width: '100%',
                flexShrink: 0
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#1f2937' }}>
                  <Package size={24} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  {editingFilter ? 'Edit Filter' : 'Add Filter'}
                </h2>
                <button
                  onClick={() => {
                    setAddingFilter(false);
                    setEditingFilter(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <X size={24} color="#6b7280" />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Filter Type Selection (only for adding new) */}
                {!editingFilter && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      Filter Type *
                    </label>
                    <CustomSelect
                      value={filterFormData.filter_type_id}
                      onChange={(value) => setFilterFormData({ ...filterFormData, filter_type_id: parseInt(value) })}
                      placeholder="Select filter type..."
                      options={allFilterTypes
                        .filter(ft => !equipmentFilters.find(ef => ef.filter_type_id === ft.filter_type_id && ef.is_active))
                        .map(ft => ({
                          value: ft.filter_type_id,
                          label: ft.filter_name
                        }))}
                    />
                  </div>
                )}

                {editingFilter && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                      Filter Type
                    </label>
                    <div style={{ padding: '10px 12px', backgroundColor: '#f3f4f6', borderRadius: '6px', fontSize: '14px', color: '#6b7280' }}>
                      {editingFilter.filter_type_name}
                    </div>
                  </div>
                )}

                {/* Part Number */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Part Number *
                  </label>
                  <input
                    type="text"
                    value={filterFormData.part_number}
                    onChange={(e) => setFilterFormData({ ...filterFormData, part_number: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter part number"
                  />
                </div>

                {/* Manufacturer */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Manufacturer (Optional)
                  </label>
                  <input
                    type="text"
                    value={filterFormData.manufacturer}
                    onChange={(e) => setFilterFormData({ ...filterFormData, manufacturer: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter manufacturer"
                  />
                </div>

                {/* Service Interval */}
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Service Interval (Hours)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={filterFormData.service_interval_hours}
                    onChange={(e) => {
                      // Only allow numeric input
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setFilterFormData({ ...filterFormData, service_interval_hours: value });
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., 250, 500, 1000"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  onClick={() => {
                    setAddingFilter(false);
                    setEditingFilter(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFilter}
                  disabled={!filterFormData.filter_type_id || !filterFormData.part_number}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: (!filterFormData.filter_type_id || !filterFormData.part_number) ? '#cbd5e1' : '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: (!filterFormData.filter_type_id || !filterFormData.part_number) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {editingFilter ? 'Update' : 'Add'} Filter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MachineHub;
